import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { uniqBy } from 'lodash';
import {
  Bar,
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  ComposedChart,
  Line,
  ReferenceLine,
} from 'recharts';
import { ChartLegend } from '../../../../../../../../components/chart-legend/chart-legend';
import { CheckboxesOptions } from '../../../../../../../../components/checkbox-select';
import { useBackdropContext } from '../../../../../../../../context/backdrop';
import { AggregateVectorParameterTypeInternal } from '../../../../../../../../data-access/gql-types/graphql';
import { MeasurementRange } from '../../../../../../../../types';
import { RangeFromTo } from '../../../../../../../../types/charts';
import { ChartDataItem, ChartDataValues } from '../../types';
import {
  extractUniqueSeries,
  lineChartParameters,
  priceAndCostParameters,
  sortedChartsParameters,
} from '../../utils/helpers';
import { generateEmptyData } from '../../utils/range';
import { EmptyState } from './empty-state';
import './index.scss';
import { LoadingState } from './loading-state';
import { CustomTooltip } from './tooltip';

type AnalysisChartProps = {
  data: ChartDataItem[];
  activeMeasurementRange: MeasurementRange;
  isLoading: boolean;
  aggregatedParameters: CheckboxesOptions<AggregateVectorParameterTypeInternal>[];
  itemPrefix: string;
  fromTo: RangeFromTo;
  noData: boolean;
  error?: string;
};

const strokeLineColor = '#9A9BA2';

export const AnalysisChart: React.FC<AnalysisChartProps> = ({
  data,
  activeMeasurementRange,
  isLoading,
  aggregatedParameters,
  itemPrefix,
  fromTo,
  error,
  noData,
}) => {
  const { t } = useTranslation('channel-details');
  const { preventCarouselSwipe, unblockCarouselSwipe } = useBackdropContext();
  const [chartData, setChartData] = useState<ChartDataItem[]>([]);

  useEffect(() => {
    const allDatesFromRange = generateEmptyData(activeMeasurementRange, fromTo);

    const chart = allDatesFromRange.map((el) => {
      const foundDataPerDate = data.find((x) => x.tooltipTime === el.tooltipTime);

      if (!foundDataPerDate) {
        return { ...el };
      } else {
        const values = foundDataPerDate.values.sort(
          (a: ChartDataValues, b: ChartDataValues) =>
            sortedChartsParameters.findIndex((f) => f === a.type) -
            sortedChartsParameters.findIndex((f) => f === b.type),
        );
        const dateKeyValue = values.reduce(
          (prev, curr) => ({ ...prev, [`${curr.type}_${curr.index}`]: curr.value }),
          {},
        );

        return {
          date: foundDataPerDate.date,
          tooltipTime: foundDataPerDate.tooltipTime,
          time: foundDataPerDate.time,
          ...dateKeyValue,
          values,
        };
      }
    });

    setChartData(chart);
  }, [data, activeMeasurementRange, fromTo]);

  const xAxisInterval = useMemo(() => {
    switch (activeMeasurementRange) {
      case MeasurementRange.DayInHours:
        return 3;
      case MeasurementRange.MonthInDays:
        return 3;
      case MeasurementRange.WeekInDays:
      case MeasurementRange.YearInMonths:
        return 'preserveStartEnd';
      case MeasurementRange.TenYearsInYears:
        return 'preserveStartEnd';
      default:
        return 1;
    }
  }, [activeMeasurementRange]);

  const onlyPriceSelected = useMemo(
    () =>
      aggregatedParameters.filter((x) => x.checked).length === 1 &&
      !!aggregatedParameters.find((x) => x.value === AggregateVectorParameterTypeInternal.Price)?.checked,
    [aggregatedParameters],
  );

  const secondYAxisShouldBeVisible = useMemo(
    () => !!chartData.find((x) => x.values?.find((y) => priceAndCostParameters.includes(y.type))),
    [chartData],
  );

  const isPriceOrCost = (type: AggregateVectorParameterTypeInternal) => priceAndCostParameters.includes(type);

  const preventScroll = () => preventCarouselSwipe(true);

  const allSeries = extractUniqueSeries(chartData);
  const legendData = uniqBy(allSeries, 'type').map((x) => {
    let sectionLabel = x.type as string;

    if (
      activeMeasurementRange === MeasurementRange.DayInHours &&
      [
        AggregateVectorParameterTypeInternal.VectorBalancedActiveEnergyIncoming,
        AggregateVectorParameterTypeInternal.VectorBalancedActiveEnergyOutgoing,
      ].includes(x.type as AggregateVectorParameterTypeInternal)
    ) {
      sectionLabel = 'VECTOR_BALANCED_ACTIVE_ENERGY';
    }

    return {
      label: t(`energyMeasurements.types.summary.${sectionLabel}`),
      color: x.color,
    };
  });

  const tickFormatterCustom = (value: number) => (Math.round(Number(value) * 1000) / 1000).toString();

  const emptyData = !chartData || !chartData.length || noData;

  if (isLoading) return <LoadingState />;
  if (emptyData || error) return <EmptyState missingData />;

  return (
    <>
      <div className="chart" onTouchStart={preventScroll} onTouchEnd={unblockCarouselSwipe}>
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={chartData} margin={{ top: 8, right: 10, left: 10, bottom: 0 }} stackOffset="sign">
            <CartesianGrid vertical={false} stroke={strokeLineColor} strokeOpacity={0.2} />
            <Tooltip content={<CustomTooltip itemPrefix={itemPrefix} active={undefined} payload={undefined} />} />
            <XAxis
              tickMargin={8}
              dataKey="time"
              axisLine={false}
              interval={xAxisInterval}
              tickLine={{ stroke: 'transparent' }}
            />
            <YAxis
              type="number"
              axisLine={{ strokeWidth: '0' }}
              tickCount={6}
              tickMargin={6}
              tickLine={{ stroke: strokeLineColor, strokeWidth: '0.5px', strokeOpacity: '0.2' }}
              yAxisId="left"
              label={{ value: t('energy'), angle: -90, position: 'left', dx: 8 }}
              tickFormatter={tickFormatterCustom}
              // domain={[(dataMin) => Math.floor(dataMin), (dataMax) => Math.ceil(dataMax)]}
            />
            <ReferenceLine y={0} stroke="#9A9BA2" yAxisId="left" />
            {secondYAxisShouldBeVisible && (
              <YAxis
                type="number"
                axisLine={{ strokeWidth: '0' }}
                tickCount={6}
                tickMargin={6}
                tickLine={{ stroke: strokeLineColor, strokeWidth: '0.5px', strokeOpacity: '0.2' }}
                yAxisId="right"
                orientation="right"
                label={{ value: t('price'), angle: 90, position: 'right', dx: -20 }}
                tickFormatter={tickFormatterCustom}
              />
            )}
            {allSeries.length
              ? allSeries.map((item) => {
                  return !lineChartParameters.includes(item.type) || onlyPriceSelected ? (
                    <Bar
                      key={`${item.type}_${item.index}`}
                      dataKey={`${item.type}_${item.index}`}
                      fill={item.color}
                      stackId="energy"
                      yAxisId={!onlyPriceSelected ? 'left' : 'right'}
                    />
                  ) : item.index === 0 ? (
                    <Line
                      dot={!!priceAndCostParameters.includes(item.type)}
                      key={`${item.type}_${item.index}`}
                      type="monotone"
                      dataKey={`${item.type}_${item.index}`}
                      stroke={item.color}
                      strokeWidth={isPriceOrCost(item.type) ? 2 : 1}
                      yAxisId={isPriceOrCost(item.type) ? 'right' : 'left'}
                      strokeDasharray={isPriceOrCost(item.type) ? '0' : '5 5'}
                    />
                  ) : null;
                })
              : null}
          </ComposedChart>
        </ResponsiveContainer>
        <ChartLegend items={legendData} dotSize={12} fontSize={12} className="m-t-16" />
      </div>
    </>
  );
};
